import "../styles/IndicatorsStyle.css";

import { useEffect, useState } from "react";

import { base_url, get_data } from "../Geoportal";
import { get_info_element } from "./Indicators";
import { customoptions, get_select_element } from "./Utils";

// dataset_id -> the exact CombinedProductAsset key the pipeline reports to
// (see adma_data_pipeline forecast_combine/job.py SOURCES / dataset_id).
const DATASET_IDS = {
  era5: "combined_drought_forecast_era5",
  gpcc: "combined_drought_forecast_gpcc",
};

// Same 5-class signed-forecast palette as the pipeline's styles module /
// backend LayerStyle "combined_forecast" -- kept in sync manually, same as
// CombinedHazard's legend (no live legend endpoint for a TiTiler COG).
export const COMBINED_FORECAST_LEGEND = [
  { color: "#2166ac", label: "Full-confidence wetter" },
  { color: "#67a9cf", label: "Leaning wetter" },
  { color: "#f7f7f7", label: "Near-normal / conflicting" },
  { color: "#ef8a62", label: "Leaning drier" },
  { color: "#b2182b", label: "Full-confidence drier" },
];

// Pulls one combined-forecast dataset's catalog entry -- visualization block
// for either the latest composite (year/period omitted) or a specific
// historical one -- plus available_periods, the {year, period} pairs the
// pipeline has ever reported (forecast_combine's historical backfill, see
// adma_data_pipeline/job.py run_forecast_combine period/year kwargs).
const fetch_combined_forecast_entry = async (source, year, period) => {
  let url = `${base_url}/api/data_api/rs_data/get_dataset_catalog?key=${DATASET_IDS[source]}`;
  if (year && period) {
    url += `&year=${year}&period=${encodeURIComponent(period)}`;
  }
  const catalog = await get_data(url);
  const entry = (catalog?.datasets || []).find((d) => d.key === DATASET_IDS[source]);
  if (!entry) {
    console.warn(`combined forecast: ${DATASET_IDS[source]} not found in get_dataset_catalog`);
  } else if (!entry?.visualization?.tile_url) {
    console.warn(`combined forecast: ${DATASET_IDS[source]} has no visualization block`, entry);
  }
  return entry;
};

// available_periods ([{year, period}, ...], newest first) -> {years, periodsByYear}
// so the dropdowns can offer only the (year, period) combinations the
// pipeline has actually produced, same constraint idea as Drought
// Persistence's getAllowedMonths/DekadsDroughtPersistence.
const index_periods = (available_periods) => {
  const periods_by_year = {};
  (available_periods || []).forEach(({ year, period }) => {
    if (!periods_by_year[year]) periods_by_year[year] = [];
    if (!periods_by_year[year].includes(period)) periods_by_year[year].push(period);
  });
  const years = Object.keys(periods_by_year)
    .map(Number)
    .sort((a, b) => b - a);
  return { years, periods_by_year };
};

const CombinedForecast = ({
  source, // "era5" | "gpcc"
  label,
  combined_forecast_check,
  setCombined_forecast_check,
  setCurrent_combined_forecast,
}) => {
  const [selected_dates, setSelected_dates] = useState({ year: null, period: null });
  const [available_years, setAvailable_years] = useState([]);
  const [periods_by_year, setPeriods_by_year] = useState({});

  const oncheck_combined_forecast = () => {
    setCombined_forecast_check(!combined_forecast_check);
  };

  const apply_entry = (entry) => {
    const { years, periods_by_year: by_year } = index_periods(entry?.available_periods);
    setAvailable_years(years);
    setPeriods_by_year(by_year);
    const slice = entry?.visualization?.slice;
    if (slice?.year && slice?.period) {
      setSelected_dates({ year: slice.year, period: slice.period });
    }
    setCurrent_combined_forecast(entry?.visualization?.tile_url || null);
  };

  useEffect(() => {
    let cancelled = false;
    if (combined_forecast_check) {
      // Reuse whatever period is already selected (so toggling off/on keeps
      // the user's choice); first-ever check has no selection yet, so
      // year/period are both null and the fetch falls through to "latest".
      fetch_combined_forecast_entry(source, selected_dates.year, selected_dates.period)
        .then((entry) => {
          if (!cancelled) apply_entry(entry);
        })
        .catch((error) => {
          console.error(`Failed to fetch combined forecast (${source}) tile_url:`, error);
          if (!cancelled) setCurrent_combined_forecast(null);
        });
    } else {
      setCurrent_combined_forecast(null);
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [combined_forecast_check]);

  const refetch_for = (year, period) => {
    fetch_combined_forecast_entry(source, year, period)
      .then((entry) => setCurrent_combined_forecast(entry?.visualization?.tile_url || null))
      .catch((error) => {
        console.error(`Failed to fetch combined forecast (${source}) for ${year}/${period}:`, error);
        setCurrent_combined_forecast(null);
      });
  };

  const onchange_year = (option) => {
    const year = option.value;
    const periods = periods_by_year[year] || [];
    const period = periods.includes(selected_dates.period) ? selected_dates.period : periods[0];
    setSelected_dates({ year, period });
    refetch_for(year, period);
  };

  const onchange_period = (option) => {
    const period = option.value;
    setSelected_dates((prev) => ({ ...prev, period }));
    refetch_for(selected_dates.year, period);
  };

  const period_options = periods_by_year[selected_dates.year] || [];

  return (
    <div className="selection-section">
      <div className="selection-section-header">
        <label className="switch">
          <input
            type="checkbox"
            defaultChecked={combined_forecast_check}
            onChange={oncheck_combined_forecast}
            disabled={false}
          />
          <span className="slider round"></span>
        </label>
        <p className="indicator-description combined-hazard-description">{label}</p>
        {get_info_element()}
      </div>

      {combined_forecast_check && selected_dates.year && (
        <p
          style={{
            position: "relative",
            color: "#000000",
            fontSize: "10px",
            left: "5px",
            marginTop: "10px",
          }}
        >
          {selected_dates.year} {selected_dates.period}
        </p>
      )}

      <div className="selection-row">
        {get_select_element(
          customoptions,
          available_years,
          onchange_year,
          selected_dates.year,
          combined_forecast_check
        )}
        {get_select_element(
          customoptions,
          period_options,
          onchange_period,
          selected_dates.period,
          combined_forecast_check
        )}
      </div>
    </div>
  );
};

export default CombinedForecast;
